import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useSnackbar } from "notistack";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { setCurrentView } from "../../state/reducers/viewReducer";
import { RootState } from "../../state/store";
import { DateSelectCrumb } from "../../components/Breadcrumbs/DateSelectCrumb";
import { ShiftSelectCrumb } from "../../components/Breadcrumbs/Shift/ShiftSelectCrumb";
import { ButtonCrumb } from "../../components/Breadcrumbs/ButtonCrumb";
import { ListSelectCrumb } from "../../components/Breadcrumbs/ListSelectCrumb";
import { DiggingLocationDTO } from "../../DTOs/Production/DiggingLocationDTO";
import { PagedResult } from "../../DTOs/Common/PagedResult";
import { plantAjax } from "../../ajax/PlantAjax";
import { useOptions } from "../../components/Hooks/useOptions";
import { diggingAjax } from "../../ajax/DiggingAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { referencesAjax } from "../../ajax/ReferencesAjax";
import { LocationDTO } from "../../DTOs/Production/LocationDTO";
import useShiftTimes from "../../components/Hooks/useShiftTimes";
import { useScrollAndPulseRow } from "../../components/Hooks/useScrollAndPulseRow";
import { setSelectedDate, setSelectedShift } from "../../state/reducers/shiftReducer";
import { PlantDTO } from "../../DTOs/Plants/PlantDTO";
import { DoubleShift } from "../../types/Shift";
import { useSignalRContext } from "../../components/Contexts/SignalRContext";
import { ProductionUpdateNotificationDTO } from "../../types/Notifications/ProductionUpdateNotificationDTO";
import { productionAjax } from "../../ajax/ProductionAjax";
import BlastMasterIdsLookupDTO from "../../DTOs/Production/BlastMasterIdsLookupDTO";
import { TrucksTable } from "./TrucksTable/TrucksTable";
import { LocationsTable } from "./LocationsTable/LocationsTable";

export const Production: React.FC = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const currentView = useSelector((s: RootState) => s.view.currentView);
  const { selectedDate, selectedShift, } = useShiftTimes();
  const { connection } = useSignalRContext();
  const { enqueueSnackbar } = useSnackbar();

  const [searchParams] = useSearchParams();
  const initialDiggingPatternId = searchParams.get("diggingPatternId");
  const initialHour = searchParams.get("hour");
  const initialLoadingId = searchParams.get("loadingId");
  const hourQuery = initialHour ? Number(initialHour) : null;
  const loadingIdQuery = initialLoadingId ? Number(initialLoadingId) : null;

  const [hourQueryString, setHourQueryString] = useState<number | null>(initialHour ? Number(initialHour) : null);
  const [loadingIdQueryString, setLoadingIdQueryString] = useState<number | null>(initialLoadingId ? Number(initialLoadingId) : null)
  const [diggingPatternIdQueryString, setDiggingPatternIdQueryString] = useState<number | null>(initialDiggingPatternId ? Number(initialDiggingPatternId) : null);
  const [highlightedLocationRowKey, setHighlightedLocationRowKey] = useState<string | null>(null);
  const redirectPatternRef = useRef<number | null>(null);

  const [selectedPlant, setSelectedPlant] = useState<PlantDTO | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<DiggingLocationDTO | null>(null);
  const [locationsTableData, setLocationsTableData] = useState<DiggingLocationDTO[]>([]);

  const clearProductionQueryParams = useCallback(() => {
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete("diggingPatternId");
    newUrl.searchParams.delete("hour");
    newUrl.searchParams.delete("loadingId");
    window.history.replaceState({}, "", newUrl.pathname + newUrl.search);
  }, []);

  const onTruckNavConsumed = useCallback(() => {
    clearProductionQueryParams();
    setHourQueryString(null);
    setLoadingIdQueryString(null);
  }, [clearProductionQueryParams]);

  const registerRefreshTrucksTableRef = useRef<(() => void) | null>(null);
  const fetchPlants = useCallback(
    () => plantAjax.getAvailableDiggers(selectedDate, selectedShift),
    [selectedDate, selectedShift]
  );

  const {
    options: destinationOptions,
    isLoading: isDestinationOptionsLoading
  } = useOptions<LocationDTO, LocationDTO>(
    ['references', 'locations'],
    () => referencesAjax.getLocations({}),
    x => x,
  )

  const {
    options: allowedNonWasteLocationTypeIds,
    isLoading: isAllowedNonWasteLocationTypeIdsLoading
  } = useOptions<number, number>(
    ['production', 'allowedNonWasteLocationTypeIds'],
    () => productionAjax.getAllowedNonWasteLocationTypeIds(),
    x => x,
  )

  const {
    options: allowedWasteLocationTypeIds,
    isLoading: isAllowedWasteLocationTypeIdsLoading
  } = useOptions<number, number>(
    ['production', 'allowedWasteLocationTypeIds'],
    () => productionAjax.getAllowedWasteLocationTypeIds(),
    x => x,
  )

  const { options: plantOptions, isLoading: isPlantOptionsLoading } = useOptions<PlantDTO, PlantDTO>(
    ['chatree', 'plants', selectedDate.toISODate(), selectedShift],
    fetchPlants,
    x => x,
    {
      onSuccessfulFetch: fetched => {
        if (diggingPatternIdQueryString) return;

        if (fetched.length > 0) {
          if (selectedPlant === null) setSelectedPlant(fetched[0]);
          else if (fetched.find(p => p.id === selectedPlant.id)) setSelectedPlant(selectedPlant);
        } else {
          setSelectedPlant(null);
        }
      }
    }
  );

  const {
    options: blastMasterOptions
  } = useOptions<BlastMasterIdsLookupDTO, BlastMasterIdsLookupDTO>(
    ['references', 'blastmasterIds', selectedDate.toISODate(), selectedShift],
    () => diggingAjax.getBlastmasterIds(selectedDate, selectedShift),
    x => x,
  );

  const getBlastOptions = useCallback(
    (pitStageId: number) => {
      return blastMasterOptions.filter(o => o.blastMasterId !== undefined && o.pitStageId === pitStageId);
    },
    [blastMasterOptions]
  );

  useEffect(() => {
    if (currentView !== "Production") {
      dispatch(setCurrentView("Production"));
    }
  }, [currentView, dispatch]);

  useEffect(() => {
    const patternId = Number(diggingPatternIdQueryString);
    if (!diggingPatternIdQueryString || patternId <= 0) return;
    if (redirectPatternRef.current === patternId) return;
    redirectPatternRef.current = patternId;

    const clearDiggingPatternQuery = () => {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("diggingPatternId");
      window.history.replaceState({}, "", newUrl.pathname + newUrl.search);
      setDiggingPatternIdQueryString(null);
    };

    const resolveLocationFromCache = (targetPatternId: number): boolean => {
      const dateStr = selectedDate.toISODate()!;
      const plants = queryClient.getQueryData<PlantDTO[]>(['chatree', 'plants', dateStr, selectedShift]);
      for (const plant of plants ?? []) {
        const page = queryClient.getQueryData<PagedResult<DiggingLocationDTO>>(
          ['dataTable', 'chatree', 'diggerLocations', dateStr, selectedShift, plant.id, 1, 5, null, 'asc', false, null]
        );
        const location = page?.data?.find(l => l.diggingPatternId === targetPatternId);
        if (location) {
          setSelectedPlant(plant);
          setHighlightedLocationRowKey(location.diggingPatternId.toString());
          setSelectedLocation(location);
          return true;
        }
      }
      return false;
    };

    const handleLookupFailure = () => {
      if (selectedPlantRef.current !== null) return;
      if (resolveLocationFromCache(Number(diggingPatternIdQueryString))) return;
      enqueueSnackbar("Couldn't open that digger's location. Please pick a digger from the list.", { variant: "warning", autoHideDuration: 5000 });
      if (plantOptionsRef.current.length > 0) {
        setSelectedPlant(plantOptionsRef.current[0]);
      }
    };

    const fetchExistingDiggingLocation = async () => {
      try {
        const resp = await diggingAjax.getLocationByDiggingPatternId(Number(diggingPatternIdQueryString));

        if (!ErrorResponse.isErrorResponse(resp)) {
          const dateFromApi = resp.shiftDate;
          const shiftFromApi: DoubleShift = resp.dayOrNight === "D" ? "DAY" : "NIGHT";

          dispatch(setSelectedDate(dateFromApi.toISODate()!));
          dispatch(setSelectedShift(shiftFromApi));

          const diggerResponse = await referencesAjax.getPlant(resp.plantId);
          if (!ErrorResponse.isErrorResponse(diggerResponse)) {
            setSelectedPlant(diggerResponse);
          }

          setHighlightedLocationRowKey(resp.diggingLocation.diggingPatternId.toString());
          setSelectedLocation(resp.diggingLocation);
        } else {
          handleLookupFailure();
        }
      } catch {
        handleLookupFailure();
      } finally {
        clearDiggingPatternQuery();
      }
    };

    fetchExistingDiggingLocation();
  }, [diggingPatternIdQueryString, dispatch, enqueueSnackbar]);

  useEffect(() => {
    if (!hourQuery && !loadingIdQuery) return;
    if (!selectedLocation) return;

    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete("hour");
    newUrl.searchParams.delete("loadingId");

    window.history.replaceState({}, "", newUrl.pathname + newUrl.search);
  }, [hourQuery, loadingIdQuery, selectedLocation]);

  const { pulseRowKey } = useScrollAndPulseRow(
    highlightedLocationRowKey,
    !isPlantOptionsLoading,
    useCallback(() => setHighlightedLocationRowKey(null), [])
  );

  const plantOptionsRef = useRef<PlantDTO[]>([]);
  plantOptionsRef.current = plantOptions;

  const selectedDateRef = useRef(selectedDate);
  const selectedShiftRef = useRef(selectedShift);
  const selectedPlantRef = useRef(selectedPlant);
  const selectedLocationRef = useRef(selectedLocation);

  useEffect(() => {
    selectedDateRef.current = selectedDate;
    selectedShiftRef.current = selectedShift;
    selectedPlantRef.current = selectedPlant;
    selectedLocationRef.current = selectedLocation;
  }, [selectedDate, selectedLocation, selectedPlant, selectedShift]);

  const handleEntryUpdated = useCallback(async (updatedEntryInfo: ProductionUpdateNotificationDTO) => {
    if (!updatedEntryInfo || !updatedEntryInfo.loadingId ||
      updatedEntryInfo.shiftDate.toUTC() < DateTime.utc(selectedDateRef.current.year, selectedDateRef.current.month, selectedDateRef.current.day, 0, 0, 0).startOf('day') ||
      updatedEntryInfo.shiftDate.toUTC() > DateTime.utc(selectedDateRef.current.year, selectedDateRef.current.month, selectedDateRef.current.day, 0, 0, 0).startOf('day') ||
      updatedEntryInfo.dayOrNight !== selectedShiftRef.current[0] ||
      updatedEntryInfo.diggerId !== selectedPlantRef.current?.id ||
      updatedEntryInfo.diggingPatternId !== selectedLocationRef.current?.diggingPatternId
    ) return;

    registerRefreshTrucksTableRef.current?.();
    enqueueSnackbar(`An entry has been updated`, { variant: 'info', autoHideDuration: 5000 });
  }, [enqueueSnackbar]);

  const handleEntryAdded = useCallback((addedEntryInfo: ProductionUpdateNotificationDTO) => {
    if (!addedEntryInfo || !addedEntryInfo.loadingId ||
      addedEntryInfo.shiftDate.toUTC() < DateTime.utc(selectedDateRef.current.year, selectedDateRef.current.month, selectedDateRef.current.day, 0, 0, 0).startOf('day') ||
      addedEntryInfo.shiftDate.toUTC() > DateTime.utc(selectedDateRef.current.year, selectedDateRef.current.month, selectedDateRef.current.day, 0, 0, 0).startOf('day') ||
      addedEntryInfo.dayOrNight !== selectedShiftRef.current[0] ||
      addedEntryInfo.diggerId !== selectedPlantRef.current?.id ||
      addedEntryInfo.diggingPatternId !== selectedLocationRef.current?.diggingPatternId
    ) return;

    registerRefreshTrucksTableRef.current?.();
    enqueueSnackbar(`A new entry has been added`, { variant: 'info', autoHideDuration: 5000 });
  }, [enqueueSnackbar]);

  const handleEntryDeleted = useCallback((deletedEntryInfo: ProductionUpdateNotificationDTO) => {
    if (!deletedEntryInfo || !deletedEntryInfo.loadingId ||
      deletedEntryInfo.shiftDate.toUTC() < DateTime.utc(selectedDateRef.current.year, selectedDateRef.current.month, selectedDateRef.current.day, 0, 0, 0).startOf('day') ||
      deletedEntryInfo.shiftDate.toUTC() > DateTime.utc(selectedDateRef.current.year, selectedDateRef.current.month, selectedDateRef.current.day, 0, 0, 0).startOf('day') ||
      deletedEntryInfo.dayOrNight !== selectedShiftRef.current[0] ||
      deletedEntryInfo.diggerId !== selectedPlantRef.current?.id ||
      deletedEntryInfo.diggingPatternId !== selectedLocationRef.current?.diggingPatternId
    ) return;

    registerRefreshTrucksTableRef.current?.();
    enqueueSnackbar(`An entry has been updated`, { variant: 'info', autoHideDuration: 5000 });
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (!connection) return;
    connection.on("DataUpdatedAsync", handleEntryUpdated);
    connection.on("DataInsertedAsync", handleEntryAdded);
    connection.on("DataDeletedAsync", handleEntryDeleted);
    return () => {
      connection.off("DataUpdatedAsync", handleEntryUpdated);
      connection.off("DataInsertedAsync", handleEntryAdded);
      connection.off("DataDeletedAsync", handleEntryDeleted);
    };
  }, [connection, handleEntryAdded, handleEntryDeleted, handleEntryUpdated]);

  return (
    <>
      <Breadcrumbs>
        <DateSelectCrumb
          selectedDate={selectedDate}
          setSelectedDate={d => { dispatch(setSelectedDate(d.toISODate()!)); setSelectedLocation(null); }}
        />
        <ShiftSelectCrumb
          selectedShift={selectedShift}
          setSelectedShift={s => { dispatch(setSelectedShift(s)); setSelectedLocation(null); }}
        />
        <ListSelectCrumb<PlantDTO>
          options={plantOptions}
          isLoading={isPlantOptionsLoading}
          selectedOption={selectedPlant}
          setSelectedOption={p => { setSelectedPlant(p); setSelectedLocation(null); }}
          getOptionValue={p => p.id}
          getOptionLabel={p => p.plantRef}
        />
        <ButtonCrumb buttonText="Production" />
      </Breadcrumbs>

      {!selectedPlant && isPlantOptionsLoading && <div className="mt-4 flex items-center justify-center text-theme-text animate-fade-in"><FontAwesomeIcon icon={faSpinner} spin size="2xl" /></div>}

      {!selectedPlant && !isPlantOptionsLoading &&
        <div className="mt-4 px-4 w-full max-w-[1480px] mx-auto text-theme-text">
          <h2 className="text-2xl font-bold mb-2 text-center">
            {`No digger ${plantOptions.length === 0 ? "available" : "selected"}`}
          </h2>
          <p className="text-center">
            {plantOptions.length === 0
              ? "No diggers available in the selected date and shift."
              : "Please select a digger first."}
          </p>
        </div>
      }

      {selectedPlant && (
        <LocationsTable
          key={`${selectedDate.toISODate()}|${selectedShift}|${selectedPlant.id}`}
          selectedDate={selectedDate}
          selectedShift={selectedShift}
          diggerId={selectedPlant.id}
          selectedLocation={selectedLocation}
          setSelectedLocation={setSelectedLocation}
          highlightedLocationRowKey={highlightedLocationRowKey}
          pulseRowKey={pulseRowKey}
          getBlastOptions={getBlastOptions}
          onLocationsChanged={setLocationsTableData}
        />
      )}

      {selectedPlant && selectedLocation && (
        <div className="mt-8 short:mt-4 w-full container mx-auto">
          <TrucksTable
            key={`${selectedDate.toISODate()}|${selectedShift}|${selectedPlant.id}|${selectedLocation.diggingPatternId}`}
            selectedDate={selectedDate}
            selectedShift={selectedShift}
            selectedLocation={selectedLocation}
            availableLocations={locationsTableData}
            destinationOptions={destinationOptions}
            allowedNonWasteLocationTypeIds={allowedNonWasteLocationTypeIds}
            allowedWasteLocationTypeIds={allowedWasteLocationTypeIds}
            isDestinationOptionsDataLoading={
              isDestinationOptionsLoading ||
              isAllowedNonWasteLocationTypeIdsLoading ||
              isAllowedWasteLocationTypeIdsLoading
            }
            registerUpdateTrucksTableRowFn={(fn) => { registerRefreshTrucksTableRef.current = fn; }}
            initialHourIndex={hourQueryString}
            initialLoadingId={loadingIdQueryString}
            onNavigationConsumed={onTruckNavConsumed}
          />
        </div>
      )}
    </>
  );
};

export default Production;
